/**
 * Scene model for the tactical board.
 *
 * A `Board` has one `BoardSnapshot`. A snapshot owns the field configuration
 * and an ordered list of `Frame`s. Each frame holds a complete `Scene` — the
 * placement of every token, stroke, shape and text on the pitch at that
 * keyframe. Animation between frames is computed at render time by linear
 * interpolation of token positions (see `animator.ts` in Phase 4).
 *
 * IDs are random UUIDs (`crypto.randomUUID()`); positions are stored in the
 * board's logical coordinate space (`BOARD_WIDTH × BOARD_HEIGHT`), which the
 * Stage scales to fit the viewport.
 */

/**
 * Logical pitch coordinate space.
 *
 * The short axis is 1920/√2 ≈ 1358, which gives the board the DIN A aspect
 * ratio. Every DIN A format — A5 through A0 — shares that same ratio, so the
 * board fills any of them completely when exported: no letterbox bars, no
 * stretching, no cropping. That is what makes the A2 poster export work.
 *
 * Snapshots written before this change use `LEGACY_BOARD_HEIGHT` and are
 * migrated when read — see `boardGeometry` on {@link BoardSnapshot} and
 * `parseSnapshot` in `repos/boards.ts`.
 */
export const BOARD_WIDTH = 1920;
export const BOARD_HEIGHT = 1358;

/**
 * Short axis of the pre-DIN (16:9) coordinate space. Only used as the source
 * dimension when migrating legacy snapshots — never for rendering.
 */
export const LEGACY_BOARD_HEIGHT = 1080;

export const FIELD_KINDS = ["pitch_green", "pitch_blank", "pitch_zones", "pitch_half"] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];

export const ORIENTATIONS = ["landscape", "portrait"] as const;
export type Orientation = (typeof ORIENTATIONS)[number];

/**
 * How a player's name is rendered on the pitch:
 *   • `full`  — the full name as stored ("Enes Yaman").
 *   • `short` — first-name initial + last name ("E. Yaman").
 */
export const NAME_DISPLAYS = ["full", "short"] as const;
export type NameDisplay = (typeof NAME_DISPLAYS)[number];

export function isFieldKind(value: unknown): value is FieldKind {
  return typeof value === "string" && (FIELD_KINDS as readonly string[]).includes(value);
}

export function isOrientation(value: unknown): value is Orientation {
  return typeof value === "string" && (ORIENTATIONS as readonly string[]).includes(value);
}

export function isNameDisplay(value: unknown): value is NameDisplay {
  return typeof value === "string" && (NAME_DISPLAYS as readonly string[]).includes(value);
}

/**
 * Format a player's name for on-pitch display. `short` keeps the last name and
 * abbreviates every leading given name to an initial ("Enes Can Yaman" →
 * "E. Yaman"). Single-word names are returned unchanged.
 */
export function formatPlayerName(name: string, mode: NameDisplay): string {
  const trimmed = name.trim();
  if (mode === "full" || !trimmed) return trimmed;
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return trimmed;
  const last = parts[parts.length - 1] as string;
  const firstInitial = (parts[0] as string).charAt(0).toUpperCase();
  return `${firstInitial}. ${last}`;
}

/**
 * Resolve the name-plate text shown under a token. The `nameDisplay`
 * abbreviation applies **only** to real roster players (those with a resolved
 * `playerName`):
 *   • real player → formatted name ("E. Yaman" in short mode).
 *   • dummy (player-kind, no roster row) → its label verbatim ("Spieler 1"),
 *     never abbreviated.
 *   • opponent → its label verbatim; a bare/legacy numeric label is presented
 *     as "Gegner N" so opponents read as "Gegner 1" rather than just "1".
 */
export function tokenDisplayName(
  token: Token,
  playerName: string | null,
  mode: NameDisplay,
): string {
  if (token.kind === "opponent") {
    const label = (token.label ?? "").trim();
    if (!label || /^\d+$/.test(label)) {
      const n = label || (token.number != null ? String(token.number) : "");
      return n ? `Gegner ${n}` : "Gegner";
    }
    return label;
  }
  // Player-kind: real roster player vs. dummy placeholder.
  if (playerName) return formatPlayerName(playerName, mode);
  return (token.label ?? "Spieler").trim();
}

/* ─── elements ───────────────────────────────────────────────────────── */

export type TokenKind = "player" | "opponent" | "ball";

export interface Token {
  id: string;
  kind: TokenKind;
  x: number;
  y: number;
  /** For `kind=player` this references `player.id` so we can resolve avatar+number. */
  playerId?: string | null;
  /** Override label/number for opponents (players resolve from their row). */
  label?: string | null;
  number?: number | null;
  /** Hex color, only used for opponents/balls. Players use position tone. */
  color?: string | null;
  /**
   * Per-token size multiplier on top of the board's default size preset.
   * Set via the Transformer handles in the editor. Missing/`null` means
   * 1 (no scaling); clamped to a sensible range on commit.
   */
  scale?: number | null;
}

/* ─── default token size presets ─────────────────────────────────────── */

export const TOKEN_SIZE_PRESETS = ["small", "normal", "large", "xl"] as const;
export type TokenSizePreset = (typeof TOKEN_SIZE_PRESETS)[number];

/** Multiplier applied to the base radius of every token on the board. */
export const TOKEN_SIZE_PRESET_FACTOR: Record<TokenSizePreset, number> = {
  small: 0.75,
  normal: 1,
  large: 1.3,
  xl: 1.6,
};

export function isTokenSizePreset(value: unknown): value is TokenSizePreset {
  return typeof value === "string" && (TOKEN_SIZE_PRESETS as readonly string[]).includes(value);
}

/** Minimum / maximum per-token scale we accept from the Transformer. */
export const TOKEN_SCALE_MIN = 0.4;
export const TOKEN_SCALE_MAX = 3;

export function clampTokenScale(scale: number): number {
  if (!Number.isFinite(scale)) return 1;
  return Math.min(TOKEN_SCALE_MAX, Math.max(TOKEN_SCALE_MIN, scale));
}

// Drawing primitives (Stroke / Shape / TextAnnotation) live in canvas-kit so
// they can be shared with other surfaces (Whiteboard, …). Re-exported here
// for source compatibility with existing tactics imports.
import type { Shape, Stroke, TextAnnotation } from "../canvas-kit/drawing/types.js";

export type {
  Shape,
  ShapeKind,
  Stroke,
  TextAnnotation,
} from "../canvas-kit/drawing/types.js";

export interface Scene {
  tokens: Token[];
  strokes: Stroke[];
  shapes: Shape[];
  texts: TextAnnotation[];
}

export function emptyScene(): Scene {
  return { tokens: [], strokes: [], shapes: [], texts: [] };
}

/* ─── frames + snapshot ─────────────────────────────────────────────── */

/** Default transition length of a newly created frame (ms). */
export const DEFAULT_FRAME_DURATION_MS = 1500;

export interface Frame {
  id: string;
  /** Duration of the animated transition INTO this frame from the previous one (ms). */
  durationMs: number;
  scene: Scene;
}

export interface BoardSnapshot {
  fieldKind: FieldKind;
  orientation: Orientation;
  frames: Frame[];
  /**
   * Per-board default token size. Multiplies the base radius of every
   * token on the pitch. Each token can additionally store its own
   * `scale` to deviate from this default (Transformer handles).
   * Missing means `"normal"` for back-compat with older snapshots.
   */
  tokenSizePreset?: TokenSizePreset;
  /**
   * How player names are rendered on the pitch. Missing means `"full"` for
   * back-compat with older snapshots.
   */
  nameDisplay?: NameDisplay;
  /**
   * Optional snapshot cover — a small data-URL screenshot of the pitch
   * captured at save time and rendered as the card thumbnail in the
   * overview list. Not used by the editor itself.
   */
  cover?: string | null;
  /**
   * Coordinate space this snapshot's element positions live in.
   *
   * `"din"` means the √2 board (`BOARD_WIDTH × BOARD_HEIGHT`). Missing means
   * the snapshot predates the DIN geometry change and still uses the 16:9
   * space (`LEGACY_BOARD_HEIGHT`); `parseSnapshot` rescales it on read and
   * stamps this field, so nothing downstream has to know about the old space.
   */
  boardGeometry?: "din";
}

export function emptySnapshot(orientation: Orientation = "landscape"): BoardSnapshot {
  return {
    fieldKind: "pitch_green",
    orientation,
    boardGeometry: "din",
    frames: [
      {
        id: crypto.randomUUID(),
        durationMs: DEFAULT_FRAME_DURATION_MS,
        scene: emptyScene(),
      },
    ],
  };
}

/* ─── viewport ──────────────────────────────────────────────────────── */

/**
 * Shared implementation behind {@link boardDimensions} and
 * {@link legacyBoardDimensions}. The only thing that separates the two is the
 * short axis they build on, so the layout rules live here once.
 */
function boardDimensionsFor(
  orientation: Orientation,
  kind: FieldKind | undefined,
  shortAxis: number,
): { width: number; height: number } {
  if (kind === "pitch_half") {
    // Halbfeld: lange Achse halbiert (Tor → Mittellinie = 52.5 m statt 105 m),
    // kurze Achse unverändert (68 m Seitenlinie). Damit Quer/Hoch konsistent
    // zu den anderen Feldformaten bleibt (Quer = breiter als hoch, Hoch =
    // höher als breit), wird hier die jeweils KÜRZERE der beiden Achsen
    // visuell zur kurzen Bildschirmachse.
    const halfLong = BOARD_WIDTH / 2; // 960
    // `shortAxis` (1358 im DIN-Raum, 1080 im Legacy-Raum) ist in beiden Fällen
    // die GRÖSSERE der beiden Zahlen. Sie wandert auf die lange Bildschirm-
    // achse (width im Landscape, height im Portrait); max/min halten das auch
    // dann korrekt, wenn sich die Konstanten einmal verschieben.
    const longerOfTwo = Math.max(halfLong, shortAxis);
    const shorterOfTwo = Math.min(halfLong, shortAxis);
    if (orientation === "portrait") {
      return { width: shorterOfTwo, height: longerOfTwo };
    }
    return { width: longerOfTwo, height: shorterOfTwo };
  }
  const long = BOARD_WIDTH;
  if (orientation === "portrait") {
    return { width: shortAxis, height: long };
  }
  return { width: long, height: shortAxis };
}

export function boardDimensions(
  orientation: Orientation,
  kind?: FieldKind,
): {
  width: number;
  height: number;
} {
  return boardDimensionsFor(orientation, kind, BOARD_HEIGHT);
}

/**
 * Board dimensions as they were before the DIN geometry change (16:9 full
 * pitch, 1080×960 half pitch). Used as the *source* rectangle when migrating
 * a legacy snapshot into the current coordinate space; never for rendering.
 */
export function legacyBoardDimensions(
  orientation: Orientation,
  kind?: FieldKind,
): {
  width: number;
  height: number;
} {
  return boardDimensionsFor(orientation, kind, LEGACY_BOARD_HEIGHT);
}

/* ─── orientation flip ──────────────────────────────────────────────── */

/**
 * Remap every scene element when the board orientation flips so the whole
 * arrangement keeps its position *relative to the pitch* — including its
 * handedness.
 *
 * The pitch graphics **rotate** 90° between orientations, they do not merely
 * transpose: the always-shown attacking box sits on the right in landscape and
 * at the top in portrait (see `pitch.tsx`), which is a 90° counter-clockwise
 * turn (right edge → top edge). A naive transpose `(x, y) → (y, x)` is a
 * *reflection* across the diagonal — it lands each element in a plausible cell
 * but mirror-flips the arrangement's left/right relative to the attacking
 * direction (a 4-4-2 comes out as its mirror image). A rigid **rotation**
 * keeps the formation's handedness intact.
 *
 * Because `boardDimensions` swaps width/height exactly between the two
 * orientations, the rotation maps the old board box precisely onto the new one
 * — no scaling, no clamping. The turn direction is chosen so the two pitch
 * *halves* land exactly where the old transpose put them (the mapping users
 * are used to) while the handedness is corrected — i.e. it is the transpose
 * with its long-axis mirror removed:
 *   • → portrait  : CW   `(x, y) → (Hold − y, x)`     (old is landscape)
 *   • → landscape : CCW  `(x, y) → (y, Wold − x)`     (old is portrait)
 * The two are exact inverses, so flipping back restores the original layout.
 */
interface Rotator {
  /** Rotate an absolute point. */
  point(x: number, y: number): [number, number];
  /** Rotate a delta/vector (translation-free) — for endpoint offsets. */
  delta(dx: number, dy: number): [number, number];
}

function makeRotator(toPortrait: boolean, oldW: number, oldH: number): Rotator {
  if (toPortrait) {
    // CW: point (x,y) → (H − y, x); linear part (dx,dy) → (−dy, dx).
    return {
      point: (x, y) => [oldH - y, x],
      delta: (dx, dy) => [-dy, dx],
    };
  }
  // CCW: point (x,y) → (y, W − x); linear part (dx,dy) → (dy, −dx).
  return {
    point: (x, y) => [y, oldW - x],
    delta: (dx, dy) => [dy, -dx],
  };
}

function rotatePoints(points: number[], rot: Rotator): number[] {
  const out = points.slice();
  for (let i = 0; i + 1 < points.length; i += 2) {
    const [nx, ny] = rot.point(out[i] as number, out[i + 1] as number);
    out[i] = nx;
    out[i + 1] = ny;
  }
  return out;
}

function rotateShape(shape: Shape, rot: Rotator): Shape {
  // Circle stores its centre in (x, y) and its *radius* in `w` (`h` unused,
  // see drawing-layer). The radius is rotation-invariant, so only the centre
  // moves.
  if (shape.kind === "circle") {
    const [cx, cy] = rot.point(shape.x, shape.y);
    return { ...shape, x: cx, y: cy };
  }
  // Line / arrow: (x, y) is the start point and (w, h) is the endpoint delta.
  // Rotate the start as a point and the delta as a vector.
  if (shape.kind === "line" || shape.kind === "arrow") {
    const [sx, sy] = rot.point(shape.x, shape.y);
    const [dw, dh] = rot.delta(shape.w, shape.h);
    return { ...shape, x: sx, y: sy, w: dw, h: dh };
  }
  // Rect: (x, y) top-left + (w, h) size. Rotating turns the top-left corner
  // into a different corner, so rotate BOTH corners and re-normalise to a
  // top-left origin with positive width/height (w↔h swap under a 90° turn).
  const [ax, ay] = rot.point(shape.x, shape.y);
  const [bx, by] = rot.point(shape.x + shape.w, shape.y + shape.h);
  return {
    ...shape,
    x: Math.min(ax, bx),
    y: Math.min(ay, by),
    w: Math.abs(bx - ax),
    h: Math.abs(by - ay),
  };
}

/**
 * Rotate all coordinates of a scene by 90° to follow an orientation flip.
 *
 * @param scene   the scene to remap
 * @param to      the target orientation (determines CW vs CCW)
 * @param oldDims the board dimensions *before* the flip
 */
export function rotateSceneForFlip(
  scene: Scene,
  to: Orientation,
  oldDims: { width: number; height: number },
): Scene {
  const rot = makeRotator(to === "portrait", oldDims.width, oldDims.height);
  return {
    tokens: scene.tokens.map((t) => {
      const [x, y] = rot.point(t.x, t.y);
      return { ...t, x, y };
    }),
    strokes: scene.strokes.map((s) => ({ ...s, points: rotatePoints(s.points, rot) })),
    shapes: scene.shapes.map((sh) => rotateShape(sh, rot)),
    texts: scene.texts.map((t) => {
      const [x, y] = rot.point(t.x, t.y);
      return { ...t, x, y };
    }),
  };
}

/* ─── board resize ──────────────────────────────────────────────────── */

/**
 * Scale a scene from one board rectangle into another.
 *
 * Mirrors {@link rotateSceneForFlip} in shape — a small transform object
 * applied uniformly to every element type — but for a resize instead of a
 * rotation. Used in two places:
 *
 *   • migrating legacy (16:9) snapshots into the DIN board on read, and
 *   • following a `fieldKind` change, where the board rectangle also changes.
 *
 * The scale is **per-axis**, so every element keeps its position *relative to
 * the pitch*: someone standing on the penalty-box edge still stands on it
 * afterwards. The trade-off is that spacing spreads out along whichever axis
 * grew — a deliberate choice, since a formation that keeps its exact spacing
 * but loses its relationship to the markings is the more wrong of the two.
 *
 * Sizes that are meant to read as absolute do NOT scale: token radii, stroke
 * widths and font sizes stay put, matching how the renderer treats them.
 */
interface Scaler {
  /** Scale an absolute point. */
  point(x: number, y: number): [number, number];
  /** Scale a delta/vector (translation-free) — for endpoint offsets. */
  delta(dx: number, dy: number): [number, number];
  /**
   * Scale a radius. Uses the geometric mean of both axes so a circle stays a
   * circle (it is drawn from a single radius) while still growing roughly in
   * proportion to the area it marks.
   */
  radius(r: number): number;
}

function makeScaler(sx: number, sy: number): Scaler {
  const uniform = Math.sqrt(sx * sy);
  return {
    point: (x, y) => [x * sx, y * sy],
    delta: (dx, dy) => [dx * sx, dy * sy],
    radius: (r) => r * uniform,
  };
}

function scalePoints(points: number[], sc: Scaler): number[] {
  const out = points.slice();
  for (let i = 0; i + 1 < points.length; i += 2) {
    const [nx, ny] = sc.point(out[i] as number, out[i + 1] as number);
    out[i] = nx;
    out[i + 1] = ny;
  }
  return out;
}

function scaleShape(shape: Shape, sc: Scaler): Shape {
  // Circle stores its centre in (x, y) and its *radius* in `w` (`h` unused,
  // see drawing-layer) — same convention `rotateShape` relies on.
  if (shape.kind === "circle") {
    const [cx, cy] = sc.point(shape.x, shape.y);
    return { ...shape, x: cx, y: cy, w: sc.radius(shape.w) };
  }
  // Line / arrow: (x, y) is the start point and (w, h) is the endpoint delta.
  if (shape.kind === "line" || shape.kind === "arrow") {
    const [sx, sy] = sc.point(shape.x, shape.y);
    const [dw, dh] = sc.delta(shape.w, shape.h);
    return { ...shape, x: sx, y: sy, w: dw, h: dh };
  }
  // Rect: (x, y) top-left + (w, h) size — both scale on their own axis.
  const [rx, ry] = sc.point(shape.x, shape.y);
  const [rw, rh] = sc.delta(shape.w, shape.h);
  return { ...shape, x: rx, y: ry, w: rw, h: rh };
}

/**
 * Rescale all coordinates of a scene from the `from` board rectangle into the
 * `to` rectangle. Returns the scene unchanged when the rectangles match, so
 * callers can apply it unconditionally.
 */
export function scaleSceneToBoard(
  scene: Scene,
  from: { width: number; height: number },
  to: { width: number; height: number },
): Scene {
  if (from.width <= 0 || from.height <= 0) return scene;
  const sx = to.width / from.width;
  const sy = to.height / from.height;
  if (sx === 1 && sy === 1) return scene;
  const sc = makeScaler(sx, sy);
  return {
    tokens: scene.tokens.map((t) => {
      const [x, y] = sc.point(t.x, t.y);
      return { ...t, x, y };
    }),
    strokes: scene.strokes.map((s) => ({ ...s, points: scalePoints(s.points, sc) })),
    shapes: scene.shapes.map((sh) => scaleShape(sh, sc)),
    texts: scene.texts.map((t) => {
      const [x, y] = sc.point(t.x, t.y);
      return { ...t, x, y };
    }),
  };
}

/** Clamp a token position to stay inside the pitch with a safe margin. */
export function clampToPitch(
  x: number,
  y: number,
  orientation: Orientation,
  margin = 40,
  kind?: FieldKind,
): { x: number; y: number } {
  const { width, height } = boardDimensions(orientation, kind);
  return {
    x: Math.min(Math.max(x, margin), width - margin),
    y: Math.min(Math.max(y, margin), height - margin),
  };
}
